import {Button, Grid, Paper} from '@material-ui/core';
import * as AgentNewsAdministrationRouteConstants from 'AgentNewsAdministration/AgentNewsAdministrationRouteConstants';
import 'AgentNewsAdministration/components/AgentNewsEntry.sass';
import {
  changeDescription,
  changeTitle,
  clear,
  getNewsEntry,
  saveNewsEntry,
} from 'AgentNewsAdministration/ducks/newsEntry';
import {hasError, isValid} from 'AgentNewsAdministration/models/News';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import {toEither} from 'Core/models/Async';
import * as E from 'fp-ts/lib/Either';
import {flow} from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useRouteMatch} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import Alert from 'Shared/components/Alert';
import {renderAsync} from 'Shared/components/RenderAsync';
import RequiredTextInput from 'Shared/components/RequiredTextInput';
import {NoOp} from 'Shared/utilities/IOUtilities';
import {prop} from 'Shared/utilities/prop';

const AgentNewsEntry = () => {
  const {
    params: {newsEntryId},
  } = useRouteMatch<{newsEntryId: string}>();
  const newsEntryIdOption = O.fromNullable(newsEntryId);
  const actionLabel = O.fold(
    () => 'Add',
    () => 'Edit'
  )(newsEntryIdOption);
  const match = useRouteMatch();
  useBreadcrumbs(`${actionLabel} Agent News Entry`, match.url);
  const {newsEntry} = useSelector((state: ReducerState) => state.agentNewsAdministration);
  const dispatch = useDispatch();

  useEffect(() => {
    pipe(O.some(getNewsEntry), O.ap(newsEntryIdOption), O.fold(NoOp, dispatch));

    return () => {
      dispatch(clear());
    };
  }, [newsEntryId]);

  return pipe(
    newsEntry,
    toEither,
    E.fold(renderAsync, loadedNewsEntry => {
      const {title, description} = loadedNewsEntry;

      return (
        <Grid container justify="center" alignItems="center" className="agent-news-entry">
          <Grid item xs={12} lg={6}>
            <Paper>
              <form className="agent-news-entry-form">
                {hasError(loadedNewsEntry) && <Alert message={loadedNewsEntry.errorMessage} />}
                <Grid container direction="column">
                  <h1 className="agent-news-entry-form__header">{actionLabel} Agent News Entry</h1>
                  <p>Please enter the title and description of the news entry below.</p>
                  <p>
                    If you need to add a link, please use the following convention: #Link|<em>LinkText</em>|
                    <em>LinkUrl</em>#
                  </p>
                  <RequiredTextInput
                    label="Title"
                    className="agent-news-entry-form__title-input"
                    onChange={flow(prop('value'), changeTitle, dispatch)}
                    value={title}
                  />
                  <RequiredTextInput
                    label="Description"
                    className="agent-news-entry-form__description-input"
                    onChange={flow(prop('value'), changeDescription, dispatch)}
                    value={description}
                    multiline
                    inputProps={{
                      rows: 10,
                    }}
                  />
                  <Grid container justify="flex-end">
                    <Grid item>
                      <Link to={AgentNewsAdministrationRouteConstants.NewsAdministration}>
                        <Button className="agent-news-entry-form__cancel-button" variant="contained">
                          Cancel
                        </Button>
                      </Link>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        type="submit"
                        color="primary"
                        disabled={!isValid(loadedNewsEntry)}
                        onClick={() => {
                          if (isValid(loadedNewsEntry)) {
                            dispatch(saveNewsEntry(loadedNewsEntry));
                          }
                        }}
                      >
                        Save
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>
        </Grid>
      );
    })
  );
};

export default AgentNewsEntry;
